import { type FC, type MouseEvent, useEffect, useRef } from "react";
import { useField } from "react-final-form";
import {
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useVideoUpload } from "./useVideoUpload";

// [MAP] индекс степени 1024 -> ключ единицы измерения; массив полный и
// статичный (0..3), поэтому индексация по вычисленному exponent — не
// динамический ключ t(), а выбор из конечной, заранее перечисленной таблицы.
const BYTE_UNIT_KEYS = [
  "videos.videoUpload.unitBytes",
  "videos.videoUpload.unitKilobytes",
  "videos.videoUpload.unitMegabytes",
  "videos.videoUpload.unitGigabytes",
] as const;

const formatBytes = (bytes: number, t: TFunction): string => {
  if (!bytes) return `0 ${t(BYTE_UNIT_KEYS[0])}`;
  const exponent = Math.min(
    BYTE_UNIT_KEYS.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${t(BYTE_UNIT_KEYS[exponent])}`;
};

/**
 * Клон референсного UploadField (partner/components/UploadField): readOnly
 * outlined TextField, клик/drag&drop открывают выбор файла, во время загрузки
 * прогресс-бар абсолютно позиционирован позади текста. Повторный выбор файла
 * заменяет текущую загрузку (useVideoUpload.start обрывает предыдущую).
 *
 * КРИТИЧНО: уже сохранённое на сервере видео нельзя удалить отсюда — кнопка
 * DeleteIcon отменяет только загрузку ТЕКУЩЕЙ сессии (в процессе или только
 * что завершённую), а не значение, полученное из initialValues.
 */
interface VideoUploadFieldProps {
  /** Пробрасывает наружу, идёт ли сейчас загрузка (используется формой, чтобы
   * отключать submit-кнопки, пока файл не долит — см. VideoForm), и текущий
   * abort() из useVideoUpload — чтобы вызывающая сторона могла реально оборвать
   * закачку при подтверждённом закрытии формы (см. EditVideoDialog), а не
   * только заблокировать сам вызов onClose. */
  onUploadingChange?: (uploading: boolean, abort: () => void) => void;
}

export const VideoUploadField: FC<VideoUploadFieldProps> = ({
  onUploadingChange,
}) => {
  const { t } = useTranslation();
  const { input } = useField<string | null>("videoUid");
  const fileNameInput = useField<string | null>("videoFileName").input;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { state, start, abort } = useVideoUpload();
  // uid/имя файла, которые были в форме до старта текущей сессии загрузки —
  // чтобы Delete во время/сразу после загрузки откатывал именно её, а не
  // трогал значение, пришедшее с сервера.
  const previousUidRef = useRef<string | null>(null);
  const previousFileNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      state.status === "done" &&
      state.videoUid &&
      input.value !== state.videoUid
    ) {
      input.onChange(state.videoUid);
      fileNameInput.onChange(state.fileName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, state.videoUid]);

  useEffect(() => {
    onUploadingChange?.(state.status === "uploading", abort);
    // abort читает всё нужное из рефов внутри useVideoUpload, поэтому не
    // страшно, что этот эффект не перезапускается при каждой новой ссылке
    // на abort — извне вызывается актуальная логика в любом случае.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    previousUidRef.current = input.value;
    previousFileNameRef.current = fileNameInput.value;
    void start(file);
  };

  const handleOpenFileDialog = () => fileInputRef.current?.click();

  const hasSession = state.status !== "idle";
  const isUploading = state.status === "uploading";

  const handleCancelSession = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    abort();
    if (state.status === "done") {
      input.onChange(previousUidRef.current);
      fileNameInput.onChange(previousFileNameRef.current);
    }
  };

  // Во время/сразу после активной сессии загрузки — локальное имя выбранного
  // файла. Иначе — имя файла текущего/обрабатываемого видео из формы, с
  // фолбэком на uid (для мигрированных видео без имени).
  const displayValue = hasSession
    ? (state.fileName ?? "")
    : (fileNameInput.value ?? input.value ?? "");

  // Лейбл — статичный текст состояния (никогда не показывает uid), три
  // состояния как в референсе: нет активной сессии → «Загрузить файл»; во
  // время загрузки → «Загружено X из Y»; после успешной загрузки в ЭТОЙ
  // сессии → «ID загрузки». Само значение (value) при этом показывает имя
  // файла во время/после сессии или сохранённый uid — вне сессии.
  const label = isUploading
    ? t("videos.videoUpload.uploadingProgressLabel", {
        uploaded: formatBytes(state.bytesUploaded, t),
        total: formatBytes(state.bytesTotal, t),
      })
    : hasSession
      ? t("videos.videoUpload.uploadIdLabel")
      : t("videos.videoUpload.uploadFileLabel");

  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        label={label}
        value={displayValue}
        fullWidth
        variant="outlined"
        onClick={handleOpenFileDialog}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          handleFile(event.dataTransfer.files?.[0]);
        }}
        helperText={state.status === "error" ? state.error : undefined}
        error={state.status === "error"}
        sx={{
          "& .MuiInputBase-root": { cursor: "pointer" },
          "& .MuiInputBase-input": {
            cursor: "pointer",
            // Во время загрузки прогресс-бар лежит позади текста на всю
            // высоту поля — форсируем белый цвет текста, иначе он теряет
            // контраст на primary-фоне прогресс-бара (см. референсный
            // UploadField.js, класс .UploadField-input).
            ...(isUploading ? { color: "#fff" } : {}),
          },
        }}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: state.status === "done" && (
              <InputAdornment position="start">
                <DoneIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {hasSession ? (
                  <Tooltip title={t("videos.videoUpload.cancelTooltip")}>
                    <IconButton edge="end" onClick={handleCancelSession}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={t("videos.videoUpload.uploadFileLabel")}>
                    <IconButton
                      edge="end"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenFileDialog();
                      }}
                    >
                      <CloudUploadIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
      {isUploading && (
        <LinearProgress
          variant="determinate"
          value={state.progress}
          sx={{
            position: "absolute",
            top: 1,
            left: 1,
            right: 1,
            height: 56,
            borderRadius: "4px",
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*,.mkv"
        hidden
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </Box>
  );
};
